import { StrikeZone as StrikeZoneConfig } from "@aura-frontend/omnibus-az-client";
import { Button, Label, Select, Skeleton } from "@win-reality/react-ui";
import { Link2, Unlink2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import batterPlateCenter from "@/assets/batter_plate_center.svg";
import batterPlateLeft from "@/assets/batter_plate_left.svg";
import batterPlateRight from "@/assets/batter_plate_right.svg";
import batterImage from "@/assets/batter_silhouette.svg";

import { EditInterceptModal } from "./EditInterceptModal";
import { ExpansionSlider } from "./ExpansionSlider";
import { BASEBALL_DIAMETER_INCHES } from "./strikeZonePresets";
import {
  findPresetByName,
  type Sport,
  type StrikeZonePresetView,
  useStrikeZonePresets,
} from "./useStrikeZonePresets";

const BALL_EXPANSION = BASEBALL_DIAMETER_INCHES;

type StrikeZoneProps = {
  sport: Sport;
  values?: StrikeZoneConfig | null;
  isLoading?: boolean;
  onChange: (values: StrikeZoneConfig) => void;
  onSaveAsync?: (values: StrikeZoneConfig) => Promise<unknown>;
};

function StrikeZone({
  sport,
  values,
  isLoading,
  onChange,
  onSaveAsync,
}: StrikeZoneProps) {
  const { presets, isLoading: isLoadingPresets } = useStrikeZonePresets(sport);

  const presetSelectItems = useMemo(
    () =>
      presets.map((p) => ({
        label: p.medianHeight ? `${p.medianHeight} - ${p.label}` : p.label,
        value: p.name,
      })),
    [presets],
  );

  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [expandedHorizontal, setExpandedHorizontal] = useState<number>(0);
  const [expandedVertical, setExpandedVertical] = useState<number>(0);
  const [isVerticalLinked, setIsVerticalLinked] = useState(true);
  const [expandedVerticalTop, setExpandedVerticalTop] = useState<number>(0);
  const [expandedVerticalBottom, setExpandedVerticalBottom] =
    useState<number>(0);
  const [interceptOpen, setInterceptOpen] = useState(false);
  const [interceptInches, setInterceptInches] = useState(8.5);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Tracks whether the current savePayload change came from initializing via the values prop
  const isSettingFromPropRef = useRef(false);

  useEffect(() => {
    if (hasInitialized) return;
    if (values === undefined) return;
    if (presets.length === 0) return;

    if (values?.strikeZoneYInches != null) {
      setInterceptInches(values.strikeZoneYInches);
    }

    if (values?.name) {
      const match = findPresetByName(values.name, presets);
      if (match) {
        // Only suppress the resulting onChange if we actually set state from
        // props — otherwise the flag persists and silently drops the user's
        // first interaction.
        isSettingFromPropRef.current = true;
        setSelectedPreset(match.name);

        const hMatch = values.name.match(/\bh(\d+(?:\.\d+)?)\b/);
        const vtMatch = values.name.match(/\bvt(\d+(?:\.\d+)?)\b/);
        const vbMatch = values.name.match(/\bvb(\d+(?:\.\d+)?)\b/);
        const vMatch = values.name.match(/\bv(\d+(?:\.\d+)?)\b/);

        setExpandedHorizontal(hMatch ? parseFloat(hMatch[1]) : 0);

        if (vtMatch && vbMatch) {
          const vt = parseFloat(vtMatch[1]);
          const vb = parseFloat(vbMatch[1]);
          setExpandedVerticalTop(vt);
          setExpandedVerticalBottom(vb);
          setExpandedVertical(Math.min(vt, vb));
          setIsVerticalLinked(false);
        } else {
          setExpandedVertical(vMatch ? parseFloat(vMatch[1]) : 0);
          setExpandedVerticalTop(0);
          setExpandedVerticalBottom(0);
          setIsVerticalLinked(true);
        }
      }
    }

    setHasInitialized(true);
  }, [values, presets, hasInitialized]);

  const currentPreset = presets.find((p) => p.name === selectedPreset);

  const handleUnlink = useCallback(() => {
    setExpandedVerticalTop(expandedVertical);
    setExpandedVerticalBottom(expandedVertical);
    setIsVerticalLinked(false);
  }, [expandedVertical]);

  const handleLink = useCallback(() => {
    setExpandedVertical(Math.min(expandedVerticalTop, expandedVerticalBottom));
    setIsVerticalLinked(true);
  }, [expandedVerticalTop, expandedVerticalBottom]);

  const applyPresetDefaults = useCallback((preset: StrikeZonePresetView) => {
    const { defaultExpansionSide, defaultExpansionUp, defaultExpansionDown } =
      preset;
    const isLinked = defaultExpansionUp === defaultExpansionDown;

    setExpandedHorizontal(defaultExpansionSide);
    setIsVerticalLinked(isLinked);

    if (isLinked) {
      setExpandedVertical(defaultExpansionUp);
      setExpandedVerticalTop(0);
      setExpandedVerticalBottom(0);
    } else {
      setExpandedVerticalTop(defaultExpansionUp);
      setExpandedVerticalBottom(defaultExpansionDown);
      setExpandedVertical(Math.min(defaultExpansionUp, defaultExpansionDown));
    }
  }, []);

  const finalDimensions = useMemo(() => {
    if (!currentPreset) return null;

    const {
      left: presetLeft,
      right: presetRight,
      top: presetTop,
      bottom: presetBottom,
      defaultExpansionSide,
      defaultExpansionUp,
      defaultExpansionDown,
    } = currentPreset;

    // Calculate base dimensions by removing default expansions that are baked into preset values
    const leftBase = presetLeft + BALL_EXPANSION * defaultExpansionSide;
    const rightBase = presetRight - BALL_EXPANSION * defaultExpansionSide;
    const topBase = presetTop - BALL_EXPANSION * defaultExpansionUp;
    const bottomBase = presetBottom + BALL_EXPANSION * defaultExpansionDown;

    const vertTop = isVerticalLinked ? expandedVertical : expandedVerticalTop;
    const vertBottom = isVerticalLinked
      ? expandedVertical
      : expandedVerticalBottom;

    // Apply user's expansion adjustments on top of base
    const left = leftBase - BALL_EXPANSION * expandedHorizontal;
    const right = rightBase + BALL_EXPANSION * expandedHorizontal;
    const top = topBase + BALL_EXPANSION * vertTop;
    const bottom = bottomBase - BALL_EXPANSION * vertBottom;

    return { left, right, top, bottom };
  }, [
    currentPreset,
    expandedHorizontal,
    expandedVertical,
    isVerticalLinked,
    expandedVerticalTop,
    expandedVerticalBottom,
  ]);

  const buildPayload = useCallback(
    (intercept: number): StrikeZoneConfig | null => {
      if (!finalDimensions) return null;
      return {
        name: [
          selectedPreset,
          expandedHorizontal > 0 && `h${expandedHorizontal}`,
          isVerticalLinked
            ? expandedVertical > 0 && `v${expandedVertical}`
            : [
                expandedVerticalTop > 0 && `vt${expandedVerticalTop}`,
                expandedVerticalBottom > 0 && `vb${expandedVerticalBottom}`,
              ]
                .filter(Boolean)
                .join(" - "),
        ]
          .filter(Boolean)
          .join(" - "),
        bottomInches: finalDimensions.bottom,
        topInches: finalDimensions.top,
        sideFromCenterInches: finalDimensions.right,
        strikeZoneYInches: intercept,
        topIncludedExpansionInches:
          (isVerticalLinked ? expandedVertical : expandedVerticalTop) *
          BALL_EXPANSION,
        bottomIncludedExpansionInches:
          -(isVerticalLinked ? expandedVertical : expandedVerticalBottom) *
          BALL_EXPANSION,
        sideFromCenterIncludedExpansionInches:
          expandedHorizontal * BALL_EXPANSION,
        ageGroup: currentPreset?.label ?? null,
      };
    },
    [
      currentPreset,
      expandedHorizontal,
      expandedVertical,
      expandedVerticalBottom,
      expandedVerticalTop,
      finalDimensions,
      isVerticalLinked,
      selectedPreset,
    ],
  );

  const savePayload = useMemo(
    () => buildPayload(interceptInches),
    [buildPayload, interceptInches],
  );

  const handleInterceptSave = useCallback(
    async (newIntercept: number) => {
      const payload = buildPayload(newIntercept);
      if (!payload) {
        setInterceptInches(newIntercept);
        return;
      }
      // Swallow the auto-save that would fire after setInterceptInches
      // since we're saving immediately ourselves here.
      isSettingFromPropRef.current = true;
      setInterceptInches(newIntercept);
      if (onSaveAsync) {
        await onSaveAsync(payload);
      } else {
        onChange(payload);
      }
    },
    [buildPayload, onChange, onSaveAsync],
  );

  useEffect(() => {
    if (!hasInitialized || !savePayload) return;

    if (isSettingFromPropRef.current) {
      isSettingFromPropRef.current = false;
      return;
    }

    onChange(savePayload);
  }, [hasInitialized, savePayload, onChange]);

  if (isLoading || isLoadingPresets) {
    return (
      <div className="mb-8">
        <div className="text-base font-bold w-full">StrikeZone</div>

        <Skeleton className="rounded h-150 mt-4" />
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="text-base font-bold w-full">Strike Zone</div>

      <div className="w-55 mt-4">
        <Label className="mb-2 text-sm">
          Average Batter Height
          <span className="text-error">*</span>
        </Label>

        <Select
          items={presetSelectItems}
          value={selectedPreset ?? ""}
          onValueChange={(value) => {
            setSelectedPreset(value);
            const preset = presets.find((p) => p.name === value);
            if (preset) applyPresetDefaults(preset);
          }}
          placeholder="Select"
        />
      </div>

      {selectedPreset !== null && (
        <div className="flex flex-col gap-6 mt-6">
          <div className="flex flex-col gap-6">
            {/* Configuration */}
            <div className="flex flex-col gap-5">
              {/* Expanded Horizontal */}
              <ExpansionSlider
                label="Expanded Horizontal Zone"
                value={expandedHorizontal}
                onChange={setExpandedHorizontal}
              />

              {/* Expanded Vertical */}
              {isVerticalLinked ? (
                <ExpansionSlider
                  label="Expanded Vertical Zone"
                  value={expandedVertical}
                  onChange={setExpandedVertical}
                />
              ) : (
                <>
                  <ExpansionSlider
                    label="Expanded Vertical Zone (Top)"
                    value={expandedVerticalTop}
                    onChange={setExpandedVerticalTop}
                  />
                  <ExpansionSlider
                    label="Expanded Vertical Zone (Bottom)"
                    value={expandedVerticalBottom}
                    onChange={setExpandedVerticalBottom}
                  />
                </>
              )}

              {/* Link/Unlink vertical zones */}
              <button
                type="button"
                onClick={isVerticalLinked ? handleUnlink : handleLink}
                className="flex items-center gap-1.5 text-blue text-sm font-medium hover:underline w-fit"
              >
                {isVerticalLinked ? <Unlink2 size={14} /> : <Link2 size={14} />}
                {isVerticalLinked
                  ? "Unlink Vertical Zones"
                  : "Link Vertical Zones"}
              </button>
            </div>

            {/* Visualization */}
            <div className="flex items-center justify-center">
              {finalDimensions && currentPreset ? (
                <StrikeZoneVisualization
                  dimensions={finalDimensions}
                  preset={currentPreset}
                  interceptInches={interceptInches}
                  onEditIntercept={() => setInterceptOpen(true)}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      <EditInterceptModal
        open={interceptOpen}
        onOpenChange={setInterceptOpen}
        value={interceptInches}
        onSave={handleInterceptSave}
      />
    </div>
  );
}

export { StrikeZone };

// Visualization constants
const VISUALIZATION_CONFIG = {
  imageWidth: 440,
  imageHeight: 400,
  scale: 5.5,
  baseBottomOffset: 130,
  imageVerticalOffset: 15,
  gridOpacity: 0.2,
  labelOffset: 25,
  labelSideOffset: 10,
} as const;

const StrikeZoneVisualization = ({
  dimensions,
  preset,
  interceptInches,
  onEditIntercept,
}: {
  dimensions: { left: number; right: number; top: number; bottom: number };
  preset: StrikeZonePresetView;
  interceptInches: number;
  onEditIntercept?: () => void;
}) => {
  const {
    imageWidth,
    imageHeight,
    scale,
    baseBottomOffset,
    gridOpacity,
    labelOffset,
    labelSideOffset,
  } = VISUALIZATION_CONFIG;

  const strikeZoneWidth = (dimensions.right - dimensions.left) * scale;
  const strikeZoneHeight = (dimensions.top - dimensions.bottom) * scale;
  const leftOffset = imageWidth / 2 - strikeZoneWidth / 2;
  const bottomDifference = (preset.bottom - dimensions.bottom) * scale;
  const bottomOffset = baseBottomOffset - bottomDifference;

  const naturalWidth = imageWidth + 30;
  const naturalHeight = imageHeight + 65;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setFitScale(Math.min(1, entry.contentRect.width / naturalWidth));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [naturalWidth]);

  return (
    <div
      ref={wrapperRef}
      className="w-full"
      style={{
        maxWidth: `${naturalWidth}px`,
        height: `${naturalHeight * fitScale}px`,
      }}
    >
      <div
        style={{
          width: `${naturalWidth}px`,
          transform: `scale(${fitScale})`,
          transformOrigin: "top left",
        }}
      >
        <div className="relative pb-4" style={{ textAlign: "center" }}>
          <div
            className="relative flex flex-col items-center gap-4 "
            style={{
              width: `${imageWidth + 30}px`,
              height: `${imageHeight + 65}px`,
              background: "#fff",
            }}
          >
            <div
              className="relative"
              style={{
                width: `${imageWidth}px`,
                height: `${imageHeight}px`,
                transform: `translateY(${VISUALIZATION_CONFIG.imageVerticalOffset}px)`,
              }}
            >
              {/* Batter Image */}
              <img
                src={batterImage}
                alt="Batter Stance"
                className=" absolute h-90 w-50 right-0 bottom-5 object-contain object-right"
              />

              <img
                src={batterPlateLeft}
                alt="Batter Plate Left"
                className="w-32 h-20 absolute left-6 bottom-0  object-contain object-right"
              />

              <img
                src={batterPlateRight}
                alt="Batter Plate Right"
                className="w-32 h-20 absolute right-6 bottom-0 object-contain object-right"
              />

              <img
                src={batterPlateCenter}
                alt="Batter Plate Center"
                className="w-30 h-20 absolute bottom-0 left-1/2 transform -translate-x-1/2 object-contain object-right"
              />

              <div
                className="absolute rounded bg-blue opacity-30"
                style={{
                  width: `${strikeZoneWidth}px`,
                  height: `${strikeZoneHeight}px`,
                  left: `${leftOffset}px`,
                  bottom: `${bottomOffset}px`,
                }}
              />

              <div
                className="absolute bg-blue"
                style={{
                  left: `${leftOffset + strikeZoneWidth / 3}px`,
                  bottom: `${bottomOffset}px`,
                  width: "2px",
                  height: `${strikeZoneHeight}px`,
                  opacity: gridOpacity,
                }}
              />
              <div
                className="absolute bg-blue"
                style={{
                  left: `${leftOffset + (2 * strikeZoneWidth) / 3}px`,
                  bottom: `${bottomOffset}px`,
                  width: "2px",
                  height: `${strikeZoneHeight}px`,
                  opacity: gridOpacity,
                }}
              />
              <div
                className="absolute bg-blue"
                style={{
                  left: `${leftOffset}px`,
                  bottom: `${bottomOffset + strikeZoneHeight / 3}px`,
                  width: `${strikeZoneWidth}px`,
                  height: "2px",
                  opacity: gridOpacity,
                }}
              />
              <div
                className="absolute bg-blue"
                style={{
                  left: `${leftOffset}px`,
                  bottom: `${bottomOffset + (2 * strikeZoneHeight) / 3}px`,
                  width: `${strikeZoneWidth}px`,
                  height: "2px",
                  opacity: gridOpacity,
                }}
              />

              <div
                className="absolute text-sm  bg-white"
                style={{
                  left: `${leftOffset + strikeZoneWidth / 2 - 15}px`,
                  top: `${imageHeight - bottomOffset - strikeZoneHeight - labelOffset}px`,
                }}
              >
                {dimensions.top.toFixed(1)}&quot;
              </div>

              <div
                className="absolute text-sm  bg-white"
                style={{
                  left: `${leftOffset + strikeZoneWidth / 2 - 15}px`,
                  bottom: `${bottomOffset - labelOffset}px`,
                }}
              >
                {dimensions.bottom.toFixed(1)}&quot;
              </div>

              <div
                className="absolute text-sm  bg-white"
                style={{
                  right: `${imageWidth - leftOffset + labelSideOffset}px`,
                  bottom: `${bottomOffset + strikeZoneHeight / 2 - 10}px`,
                }}
              >
                {dimensions.left.toFixed(1)}&quot;
              </div>

              <div
                className="absolute text-sm  bg-white"
                style={{
                  left: `${leftOffset + strikeZoneWidth + labelSideOffset}px`,
                  bottom: `${bottomOffset + strikeZoneHeight / 2 - 10}px`,
                }}
              >
                {dimensions.right.toFixed(1)}&quot;
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-0.5">
            <p className="font-bold text-xs leading-tight max-w-30">
              Strike Zone Intercept
            </p>
            <p className="font-bold text-base  leading-tight">
              {interceptInches.toFixed(1)}&quot;
            </p>

            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={onEditIntercept}
              className="mt-2 flex items-center gap-1.5 text-blue text-sm font-medium  w-fit"
            >
              Edit Strike Zone Intercept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
